import React, {FC, PropsWithChildren, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getAnalyticsDetailsIsLoading} from '../../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';

/*
 * Props.
 */

interface MetricDetailsSectionWrapperProps {
  sectionTitle: ReactNode;
  isCollapsible?: boolean;
  itemCount?: number;
  headerRightButtons?: ReactNode;
}

/*
 * Style.
 */

const StyledMetricDetailsSectionWrapper = styled.div<{$isCollapsed: boolean}>`
  padding: 0 16px ${(p) => (p.$isCollapsed ? '0' : '20px')} 16px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledSectionTitleWrapper = styled.div`
  ${flexCenter}
  height: 56px;
`;

const StyledSectionTitle = styled.div`
  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
`;

const StyledIconButton = styled(IconButton)<{$isCollapsed: boolean}>`
  ${(p) => (p.$isCollapsed ? `transform: rotate(-90deg)` : '')}
`;

const StyledItemCount = styled.div`
  margin-right: 4px;
  margin-left: 6px;
  font-size: 10px;
  background-color: ${(p) => p.theme.greys.shade30};
  border-radius: 8px;
  width: 21px;
  height: 16px;
  ${flexCenter}
  justify-content: center;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledHeaderRightButtonsDiv = styled.div`
  display: flex;
  margin-left: auto;
`;

/*
 * Component.
 */

export const SidePanelSectionWrapper: FC<PropsWithChildren<MetricDetailsSectionWrapperProps>> = ({
  sectionTitle,
  children,
  isCollapsible = false,
  itemCount,
  headerRightButtons,
}) => {
  const isLoading = useAppSelector(getAnalyticsDetailsIsLoading);

  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <StyledMetricDetailsSectionWrapper $isCollapsed={isCollapsed}>
      <StyledSectionTitleWrapper>
        {maybeDisplaySectionMeta(
          isLoading,
          sectionTitle,
          isCollapsed,
          setIsCollapsed,
          isCollapsible,
          itemCount,
          headerRightButtons,
        )}
      </StyledSectionTitleWrapper>
      {!isCollapsed && children}
    </StyledMetricDetailsSectionWrapper>
  );
};

/*
 * Helpers.
 */

function maybeDisplaySectionMeta(
  isLoading: boolean,
  sectionTitle: ReactNode,
  isCollapsed: boolean,
  setIsCollapsed: (val: boolean) => void,
  isCollapsible: boolean,
  itemCount?: number,
  rightButtons?: ReactNode,
) {
  if (isLoading) {
    return <Skeleton height="26px" width="40%" />;
  }

  return (
    <>
      <StyledSectionTitle>{sectionTitle}</StyledSectionTitle>
      {itemCount ? <StyledItemCount>{itemCount}</StyledItemCount> : null}
      {isCollapsible && (
        <UserThemeConsumer>
          {(theme) => (
            <StyledIconButton
              $isCollapsed={isCollapsed}
              iconName="chevronExpandTiny"
              backgroundHoverColor="transparent"
              label={
                isCollapsed ? (
                  <Trans id="analytics-metrics-detail-expand-icon">Expand section</Trans>
                ) : (
                  <Trans id="analytics-metrics-detail-collapse-icon">Collapse section</Trans>
                )
              }
              color={theme.greys.shade70}
              onClick={() => setIsCollapsed(!isCollapsed)}
            />
          )}
        </UserThemeConsumer>
      )}
      {rightButtons && <StyledHeaderRightButtonsDiv>{rightButtons}</StyledHeaderRightButtonsDiv>}
    </>
  );
}
